import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit2, Save, Trash2, Plus, Syringe, Calendar, User, X } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnimalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [animal, setAnimal] = useState(null);
  const [vaccines, setVaccines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showVaccineForm, setShowVaccineForm] = useState(false);
  const [editingVaccine, setEditingVaccine] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);

  const [formData, setFormData] = useState({
    photo: '',
    species: 'Dog',
    name: '',
    gender: 'Male',
    colour: '',
    area: '',
    sterilized: false,
    vaccinated: false,
    temperament: 'Friendly',
    remarks: '',
    ownerTower: '',
    ownerFlatNo: '',
    ownerName: '',
    ownerContact: '',
  });

  const [vaccineFormData, setVaccineFormData] = useState({
    vaccineName: '',
    dateGiven: '',
    validTill: '',
    vet: '',
    certificate: '',
  });

  useEffect(() => {
    fetchAnimal();
    fetchVaccines();
  }, [id]);

  const fetchAnimal = async () => {
    try {
      const response = await axios.get(`${API}/animals/${id}`);
      setAnimal(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching animal:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVaccines = async () => {
    try {
      const response = await axios.get(`${API}/vaccines/${id}`);
      setVaccines(response.data.filter(v => v.approved));
    } catch (error) {
      console.error('Error fetching vaccines:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/animals/${id}`, formData);
      setAnimal(formData);
      setEditing(false);
    } catch (error) {
      console.error('Error updating animal:', error);
      alert('Failed to update animal. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this animal record?')) {
      try {
        await axios.delete(`${API}/animals/${id}`);
        navigate('/');
      } catch (error) {
        console.error('Error deleting animal:', error);
        alert('Failed to delete animal. Please try again.');
      }
    }
  };

  const handleVaccineChange = (e) => {
    const { name, value } = e.target;
    setVaccineFormData({
      ...vaccineFormData,
      [name]: value,
    });
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificateFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setVaccineFormData({
          ...vaccineFormData,
          certificate: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddVaccine = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vaccines`, {
        ...vaccineFormData,
        animalId: id,
      });
      fetchVaccines();
      setShowVaccineForm(false);
      setVaccineFormData({
        vaccineName: '',
        dateGiven: '',
        validTill: '',
        vet: '',
        certificate: '',
      });
    } catch (error) {
      console.error('Error adding vaccine:', error);
      alert('Failed to add vaccine record. Please try again.');
    }
  };

  const handleUpdateVaccine = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/vaccines/${editingVaccine}`, vaccineFormData);
      fetchVaccines();
      setEditingVaccine(null);
      setVaccineFormData({
        vaccineName: '',
        dateGiven: '',
        validTill: '',
        vet: '',
        certificate: '',
      });
    } catch (error) {
      console.error('Error updating vaccine:', error);
      alert('Failed to update vaccine record. Please try again.');
    }
  };

  const handleDeleteVaccine = async (vaccineId) => {
    if (window.confirm('Are you sure you want to delete this vaccine record?')) {
      try {
        await axios.delete(`${API}/vaccines/${vaccineId}`);
        fetchVaccines();
      } catch (error) {
        console.error('Error deleting vaccine:', error);
        alert('Failed to delete vaccine record. Please try again.');
      }
    }
  };

  const startEditVaccine = (vaccine) => {
    setEditingVaccine(vaccine.id);
    setVaccineFormData({
      vaccineName: vaccine.vaccineName,
      dateGiven: vaccine.dateGiven,
      validTill: vaccine.validTill,
      vet: vaccine.vet,
      certificate: vaccine.certificate || '',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-indicator">
        <div className="text-xl text-text-secondary font-medium">Loading...</div>
      </div>
    );
  }

  if (!animal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-text-secondary font-medium">Animal not found</div>
      </div>
    );
  }

  const defaultImages = {
    Dog: 'https://images.unsplash.com/photo-1644316549560-a0d17dd39978?auto=format&fit=crop&w=800&q=80',
    Cat: 'https://images.unsplash.com/photo-1765176096027-57b4c46b972f?auto=format&fit=crop&w=800&q=80'
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/')}
          data-testid="back-button"
          className="mb-6 flex items-center gap-2 text-primary font-bold hover:text-primary-hover transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden mb-8">
            <div className="relative h-64 bg-gradient-to-br from-orange-100 to-orange-50">
              <img
                src={animal.photo || defaultImages[animal.species]}
                alt={animal.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 flex gap-2">
                {!editing && (
                  <>
                    <button
                      onClick={() => setEditing(true)}
                      data-testid="edit-animal-button"
                      className="p-3 rounded-full bg-white shadow-md hover:shadow-lg hover:scale-110 transition-all text-primary"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleDelete}
                      data-testid="delete-animal-button"
                      className="p-3 rounded-full bg-white shadow-md hover:shadow-lg hover:scale-110 transition-all text-red-600"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {editing ? (
              <form onSubmit={handleUpdate} className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Species *</label>
                    <select
                      name="species"
                      value={formData.species}
                      onChange={handleChange}
                      required
                      data-testid="edit-input-species"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                    >
                      <option value="Dog">Dog</option>
                      <option value="Cat">Cat</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="edit-input-name"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Gender *</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      required
                      data-testid="edit-input-gender"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Colour *</label>
                    <input
                      type="text"
                      name="colour"
                      value={formData.colour}
                      onChange={handleChange}
                      required
                      data-testid="edit-input-colour"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Area *</label>
                    <input
                      type="text"
                      name="area"
                      value={formData.area}
                      onChange={handleChange}
                      required
                      data-testid="edit-input-area"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Temperament *</label>
                    <select
                      name="temperament"
                      value={formData.temperament}
                      onChange={handleChange}
                      required
                      data-testid="edit-input-temperament"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                    >
                      <option value="Friendly">Friendly</option>
                      <option value="Aggressive">Aggressive</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Photo URL</label>
                  <input
                    type="url"
                    name="photo"
                    value={formData.photo}
                    onChange={handleChange}
                    data-testid="edit-input-photo"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Remarks</label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    data-testid="edit-input-remarks"
                    rows="4"
                    className="w-full px-4 py-3 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none resize-none"
                  />
                </div>

                <div className="border-t-2 border-orange-100 pt-6">
                  <h3 className="text-lg font-bold text-text-primary mb-4 font-heading">Owner Details (For Pet Animals)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Tower</label>
                      <select
                        name="ownerTower"
                        value={formData.ownerTower || ''}
                        onChange={handleChange}
                        data-testid="edit-input-owner-tower"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                      >
                        <option value="">Select Tower</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Flat No</label>
                      <input
                        type="text"
                        name="ownerFlatNo"
                        value={formData.ownerFlatNo || ''}
                        onChange={handleChange}
                        data-testid="edit-input-owner-flat"
                        placeholder="e.g., 101"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Owner Name</label>
                      <input
                        type="text"
                        name="ownerName"
                        value={formData.ownerName || ''}
                        onChange={handleChange}
                        data-testid="edit-input-owner-name"
                        placeholder="Owner's name"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Owner Contact</label>
                      <input
                        type="tel"
                        name="ownerContact"
                        value={formData.ownerContact || ''}
                        onChange={handleChange}
                        data-testid="edit-input-owner-contact"
                        placeholder="+91 98765 43210"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-orange-100 bg-orange-50 cursor-pointer hover:border-primary transition-all">
                    <input
                      type="checkbox"
                      name="sterilized"
                      checked={formData.sterilized}
                      onChange={handleChange}
                      data-testid="edit-input-sterilized"
                      className="w-5 h-5 text-primary rounded focus:ring-4 focus:ring-orange-100"
                    />
                    <span className="font-bold text-text-primary">Sterilized</span>
                  </label>

                  <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-orange-100 bg-orange-50 cursor-pointer hover:border-primary transition-all">
                    <input
                      type="checkbox"
                      name="vaccinated"
                      checked={formData.vaccinated}
                      onChange={handleChange}
                      data-testid="edit-input-vaccinated"
                      className="w-5 h-5 text-primary rounded focus:ring-4 focus:ring-orange-100"
                    />
                    <span className="font-bold text-text-primary">Vaccinated</span>
                  </label>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    data-testid="save-animal-button"
                    className="flex-1 bg-primary text-white shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                  >
                    <Save className="w-5 h-5 inline mr-2" />
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setFormData(animal);
                    }}
                    data-testid="cancel-edit-button"
                    className="bg-white text-primary border-2 border-orange-100 hover:border-primary hover:bg-orange-50 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-8">
                <h1 className="text-4xl font-extrabold text-text-primary mb-4 font-heading" data-testid="animal-detail-name">{animal.name}</h1>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-sm font-bold text-text-muted mb-1">Species</div>
                    <div className="text-lg font-bold text-text-primary" data-testid="detail-species">{animal.species}</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-sm font-bold text-text-muted mb-1">Gender</div>
                    <div className="text-lg font-bold text-text-primary" data-testid="detail-gender">{animal.gender}</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-sm font-bold text-text-muted mb-1">Colour</div>
                    <div className="text-lg font-bold text-text-primary" data-testid="detail-colour">{animal.colour}</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-sm font-bold text-text-muted mb-1">Area</div>
                    <div className="text-lg font-bold text-text-primary" data-testid="detail-area">{animal.area}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-sm font-bold text-text-muted mb-1">Temperament</div>
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                      animal.temperament === 'Friendly'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`} data-testid="detail-temperament">
                      {animal.temperament}
                    </div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-sm font-bold text-text-muted mb-1">Sterilized</div>
                    <div className="text-lg font-bold text-text-primary" data-testid="detail-sterilized">{animal.sterilized ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <div className="text-sm font-bold text-text-muted mb-1">Vaccinated</div>
                    <div className="text-lg font-bold text-text-primary" data-testid="detail-vaccinated">{animal.vaccinated ? 'Yes' : 'No'}</div>
                  </div>
                </div>

                {animal.remarks && (
                  <div className="bg-orange-50 p-4 rounded-xl mb-6">
                    <div className="text-sm font-bold text-text-muted mb-2">Remarks</div>
                    <p className="text-text-primary" data-testid="detail-remarks">{animal.remarks}</p>
                  </div>
                )}

                {(animal.ownerName || animal.ownerTower || animal.ownerFlatNo || animal.ownerContact) && (
                  <div className="bg-blue-50 border-2 border-blue-200 p-6 rounded-xl">
                    <h3 className="text-lg font-bold text-text-primary mb-4 font-heading flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Owner Details (Pet Animal)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {animal.ownerTower && (
                        <div>
                          <div className="text-xs font-bold text-blue-700 mb-1">TOWER</div>
                          <div className="text-base font-bold text-text-primary" data-testid="detail-owner-tower">{animal.ownerTower}</div>
                        </div>
                      )}
                      {animal.ownerFlatNo && (
                        <div>
                          <div className="text-xs font-bold text-blue-700 mb-1">FLAT NO</div>
                          <div className="text-base font-bold text-text-primary" data-testid="detail-owner-flat">{animal.ownerFlatNo}</div>
                        </div>
                      )}
                      {animal.ownerName && (
                        <div>
                          <div className="text-xs font-bold text-blue-700 mb-1">OWNER NAME</div>
                          <div className="text-base font-bold text-text-primary" data-testid="detail-owner-name">{animal.ownerName}</div>
                        </div>
                      )}
                      {animal.ownerContact && (
                        <div>
                          <div className="text-xs font-bold text-blue-700 mb-1">OWNER CONTACT</div>
                          <a href={`tel:${animal.ownerContact}`} className="text-base font-bold text-blue-600 hover:text-blue-800 transition-colors" data-testid="detail-owner-contact">
                            {animal.ownerContact}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden">
            <div className="bg-gradient-to-br from-secondary to-secondary-light p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-xl">
                    <Syringe className="w-6 h-6 text-secondary" />
                  </div>
                  <h2 className="text-2xl font-bold text-white font-heading">Vaccine Records</h2>
                </div>
                {!showVaccineForm && !editingVaccine && (
                  <button
                    onClick={() => setShowVaccineForm(true)}
                    data-testid="add-vaccine-button"
                    className="bg-white text-secondary px-6 py-2 rounded-full font-bold hover:bg-secondary-light transition-all shadow-md hover:shadow-lg"
                  >
                    <Plus className="w-4 h-4 inline mr-2" />
                    Add Vaccine
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {(showVaccineForm || editingVaccine) && (
                <form
                  onSubmit={editingVaccine ? handleUpdateVaccine : handleAddVaccine}
                  className="mb-6 p-6 bg-secondary-light rounded-xl border-2 border-secondary/20"
                >
                  <h3 className="text-lg font-bold text-text-primary mb-4 font-heading">
                    {editingVaccine ? 'Edit Vaccine Record' : 'New Vaccine Record'}
                  </h3>
                  <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-xl mb-4">
                    <div className="text-xs font-bold text-blue-700 mb-1">ANIMAL ID</div>
                    <div className="text-sm font-bold text-text-primary font-mono" data-testid="vaccine-animal-id">{animal.id}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Vaccine Name *</label>
                      <input
                        type="text"
                        name="vaccineName"
                        value={vaccineFormData.vaccineName}
                        onChange={handleVaccineChange}
                        required
                        data-testid="vaccine-input-name"
                        placeholder="e.g., Rabies, Distemper"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-secondary focus:ring-4 focus:ring-secondary-light transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Date Given *</label>
                      <input
                        type="date"
                        name="dateGiven"
                        value={vaccineFormData.dateGiven}
                        onChange={handleVaccineChange}
                        required
                        data-testid="vaccine-input-date-given"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-secondary focus:ring-4 focus:ring-secondary-light transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Valid Till *</label>
                      <input
                        type="date"
                        name="validTill"
                        value={vaccineFormData.validTill}
                        onChange={handleVaccineChange}
                        required
                        data-testid="vaccine-input-valid-till"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-secondary focus:ring-4 focus:ring-secondary-light transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Vet Name *</label>
                      <input
                        type="text"
                        name="vet"
                        value={vaccineFormData.vet}
                        onChange={handleVaccineChange}
                        required
                        data-testid="vaccine-input-vet"
                        placeholder="Veterinarian name"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-secondary focus:ring-4 focus:ring-secondary-light transition-all outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Certificate Attachment</label>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleCertificateChange}
                        data-testid="vaccine-input-certificate"
                        className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-secondary focus:ring-4 focus:ring-secondary-light transition-all outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-secondary file:text-white hover:file:bg-secondary/90"
                      />
                      {certificateFile && (
                        <p className="text-sm text-secondary mt-2 font-medium">✓ {certificateFile.name} attached</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 mt-4">
                    <button
                      type="submit"
                      data-testid="save-vaccine-button"
                      className="bg-secondary text-white px-6 py-2 rounded-full font-bold hover:bg-secondary/90 transition-all shadow-md"
                    >
                      <Save className="w-4 h-4 inline mr-2" />
                      {editingVaccine ? 'Update' : 'Add'} Vaccine
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowVaccineForm(false);
                        setEditingVaccine(null);
                        setCertificateFile(null);
                        setVaccineFormData({
                          vaccineName: '',
                          dateGiven: '',
                          validTill: '',
                          vet: '',
                          certificate: '',
                        });
                      }}
                      data-testid="cancel-vaccine-button"
                      className="bg-white text-secondary border-2 border-secondary/20 px-6 py-2 rounded-full font-bold hover:bg-secondary-light transition-all"
                    >
                      <X className="w-4 h-4 inline mr-2" />
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {vaccines.length === 0 ? (
                <div className="text-center py-8" data-testid="no-vaccines">
                  <Syringe className="w-12 h-12 text-text-muted mx-auto mb-3" />
                  <p className="text-text-secondary font-medium">No vaccine records yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {vaccines.map((vaccine) => (
                    <div
                      key={vaccine.id}
                      data-testid={`vaccine-card-${vaccine.id}`}
                      className="bg-orange-50 p-6 rounded-xl border-2 border-orange-100 hover:border-secondary transition-all"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-xl font-bold text-text-primary font-heading" data-testid="vaccine-name">{vaccine.vaccineName}</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEditVaccine(vaccine)}
                            data-testid="edit-vaccine-button"
                            className="p-2 rounded-lg bg-white hover:bg-secondary-light text-secondary transition-all"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteVaccine(vaccine.id)}
                            data-testid="delete-vaccine-button"
                            className="p-2 rounded-lg bg-white hover:bg-red-50 text-red-600 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-2 text-text-secondary">
                          <Calendar className="w-4 h-4 text-secondary" />
                          <span className="text-sm">Given: <span className="font-bold" data-testid="vaccine-date-given">{vaccine.dateGiven}</span></span>
                        </div>
                        <div className="flex items-center gap-2 text-text-secondary">
                          <Calendar className="w-4 h-4 text-secondary" />
                          <span className="text-sm">Valid: <span className="font-bold" data-testid="vaccine-valid-till">{vaccine.validTill}</span></span>
                        </div>
                        <div className="flex items-center gap-2 text-text-secondary">
                          <User className="w-4 h-4 text-secondary" />
                          <span className="text-sm">Vet: <span className="font-bold" data-testid="vaccine-vet">{vaccine.vet}</span></span>
                        </div>
                      </div>
                      {vaccine.certificate && (
                        <p className="mt-3 text-sm text-text-secondary" data-testid="vaccine-certificate">
                          <span className="font-bold">Certificate:</span> {vaccine.certificate}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AnimalDetail;